/**
 * CookieBanner — accessible cookie-consent banner.
 *
 * Compliance posture (PIPEDA + EU/UK GDPR + Quebec Law 25):
 * - No non-essential cookies fire until the user accepts (`consent === 'all'`).
 * - "Essential only" sets `essential` so the banner stays dismissed but blocks
 *   analytics / marketing scripts.
 * - "Customize" defers to the Privacy page (`${privacyHref}#cookies`) for
 *   per-category detail. In Phase 0 we don't run any third-party analytics or
 *   advertising cookies, so the banner doesn't yet need a category picker —
 *   the privacy page documents what each option covers.
 * - Unset state (no record in localStorage) is the only state that shows the
 *   banner. PIPEDA accepts opt-in/opt-out for low-sensitivity data; GDPR and
 *   Quebec Law 25 require affirmative, specific, freely-given opt-in for
 *   non-essential cookies — both satisfied by the explicit
 *   Accept-all / Essential-only / Customize choice.
 *
 * Withdrawal of consent (GDPR Art. 7(3) + Law 25 §13):
 *   `useCookieConsent().reset` returns the user to the unset state and
 *   re-shows the banner on next render. This hook is exposed so the Privacy
 *   page can wire a "Reset cookie preferences" control. Until that wiring
 *   ships, users can clear the `northbridge_cookie_consent` key in their
 *   browser storage to revoke consent — the privacy page documents that path.
 *
 * Storage key: `northbridge_cookie_consent` (per-site to avoid cross-site
 * bleed when sub-domains share apex cookies).
 *
 * Accessibility:
 * - role="dialog" + aria-labelledby/aria-describedby
 * - focus is trapped between the in-dialog actions while the banner is open
 *   (the focus-trap query selects all focusable elements, so adding the
 *   Customize link does not break it)
 * - Escape and the dismiss button both close (treated as "essential only")
 * - the banner sits at the bottom and never overlaps the header/skip-link
 *
 * The exported `useCookieConsent()` hook returns the live consent state and
 * is what analytics integrations (e.g. Cloudflare Web Analytics agent) MUST
 * gate on — only fire on `'all'`.
 */
import { useCallback, useEffect, useRef, useState } from 'react';

export type CookieConsent = 'all' | 'essential' | 'unset';

const STORAGE_KEY = 'northbridge_cookie_consent';
const EVENT_NAME = 'launchlayer:cookie-consent-change';

function readConsent(): CookieConsent {
  if (typeof window === 'undefined') return 'unset';
  try {
    const v = window.localStorage.getItem(STORAGE_KEY);
    return v === 'all' || v === 'essential' ? v : 'unset';
  } catch {
    return 'unset';
  }
}

function writeConsent(value: Exclude<CookieConsent, 'unset'>) {
  try {
    window.localStorage.setItem(STORAGE_KEY, value);
    window.dispatchEvent(new CustomEvent(EVENT_NAME, { detail: value }));
  } catch {
    /* private mode / disabled storage — banner just won't persist */
  }
}

/**
 * `useCookieConsent` — analytics integrations should gate on the returned
 * value. Re-renders when consent changes (same tab, via custom event; cross
 * tab, via the native `storage` event).
 */
export function useCookieConsent() {
  const [consent, setConsent] = useState<CookieConsent>('unset');

  useEffect(() => {
    setConsent(readConsent());
    const onChange = () => setConsent(readConsent());
    window.addEventListener(EVENT_NAME, onChange);
    window.addEventListener('storage', onChange);
    return () => {
      window.removeEventListener(EVENT_NAME, onChange);
      window.removeEventListener('storage', onChange);
    };
  }, []);

  const acceptAll = useCallback(() => writeConsent('all'), []);
  const acceptEssential = useCallback(() => writeConsent('essential'), []);
  const reset = useCallback(() => {
    try {
      window.localStorage.removeItem(STORAGE_KEY);
      window.dispatchEvent(new CustomEvent(EVENT_NAME, { detail: 'unset' }));
    } catch {
      /* noop */
    }
  }, []);

  return { consent, acceptAll, acceptEssential, reset };
}

export default function CookieBanner({ privacyHref = '/privacy' }: { privacyHref?: string }) {
  const { consent, acceptAll, acceptEssential } = useCookieConsent();
  const [mounted, setMounted] = useState(false);
  const dialogRef = useRef<HTMLDivElement | null>(null);
  const acceptAllRef = useRef<HTMLButtonElement | null>(null);
  const dismissRef = useRef<HTMLButtonElement | null>(null);
  const lastFocusedRef = useRef<HTMLElement | null>(null);

  useEffect(() => {
    setMounted(true);
  }, []);

  const open = mounted && consent === 'unset';

  useEffect(() => {
    if (!open) return;
    lastFocusedRef.current = document.activeElement as HTMLElement | null;
    acceptAllRef.current?.focus();
    return () => {
      lastFocusedRef.current?.focus?.();
    };
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.preventDefault();
        acceptEssential();
        return;
      }
      if (e.key !== 'Tab') return;
      const root = dialogRef.current;
      if (!root) return;
      const focusables = root.querySelectorAll<HTMLElement>(
        'a[href], button:not([disabled])',
      );
      if (focusables.length === 0) return;
      const first = focusables[0];
      const last = focusables[focusables.length - 1];
      const active = document.activeElement as HTMLElement | null;
      if (e.shiftKey && active === first) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && active === last) {
        e.preventDefault();
        first.focus();
      }
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [open, acceptEssential]);

  if (!open) return null;

  return (
    <div
      ref={dialogRef}
      role="dialog"
      aria-modal="false"
      aria-labelledby="cookie-banner-title"
      aria-describedby="cookie-banner-desc"
      style={{
        position: 'fixed',
        left: 0,
        right: 0,
        bottom: 0,
        zIndex: 90,
        background: '#1f2937',
        color: '#ffffff',
        borderTop: '1px solid rgba(255,255,255,0.15)',
        boxShadow: '0 -8px 24px rgba(0,0,0,0.18)',
        padding: '1rem 1.25rem',
        fontFamily: 'inherit',
      }}
    >
      <div
        style={{
          maxWidth: '72rem',
          margin: '0 auto',
          display: 'flex',
          flexWrap: 'wrap',
          gap: '0.75rem 1rem',
          alignItems: 'center',
          justifyContent: 'space-between',
        }}
      >
        <div style={{ flex: '1 1 22rem', minWidth: 0 }}>
          <p
            id="cookie-banner-title"
            style={{ margin: 0, fontWeight: 600, fontSize: '0.95rem' }}
          >
            Cookies and your privacy
          </p>
          <p
            id="cookie-banner-desc"
            style={{
              margin: '0.25rem 0 0',
              fontSize: '0.875rem',
              lineHeight: 1.45,
              color: 'rgba(255,255,255,0.85)',
            }}
          >
            We use a small number of <strong>essential cookies</strong> to keep
            this site secure and to remember your preferences. We do not
            currently set any analytics, advertising, or third-party tracking
            cookies. If we add optional cookies in future, they will only run
            after you choose <em>Accept all</em>.
          </p>
          <p
            style={{
              margin: '0.4rem 0 0',
              fontSize: '0.8rem',
              lineHeight: 1.4,
              color: 'rgba(255,255,255,0.7)',
            }}
          >
            You can change your choice anytime via{' '}
            <a
              href={`${privacyHref}#cookies`}
              style={{ color: '#ffffff', textDecoration: 'underline' }}
            >
              Privacy Settings
            </a>
            . Required by EU/UK GDPR and Quebec Law 25.
          </p>
        </div>
        <div
          style={{
            display: 'flex',
            flexWrap: 'wrap',
            gap: '0.5rem',
            alignItems: 'center',
          }}
        >
          <button
            ref={acceptAllRef}
            type="button"
            onClick={acceptAll}
            style={{
              background: '#388e3c',
              color: '#ffffff',
              border: '1px solid #2e7d32',
              padding: '0.5rem 0.9rem',
              borderRadius: '4px',
              fontWeight: 600,
              fontSize: '0.875rem',
              cursor: 'pointer',
            }}
          >
            Accept all
          </button>
          <button
            type="button"
            onClick={acceptEssential}
            style={{
              background: 'transparent',
              color: '#ffffff',
              border: '1px solid rgba(255,255,255,0.55)',
              padding: '0.5rem 0.9rem',
              borderRadius: '4px',
              fontWeight: 600,
              fontSize: '0.875rem',
              cursor: 'pointer',
            }}
          >
            Essential only
          </button>
          <a
            href={`${privacyHref}#cookies`}
            style={{
              background: 'transparent',
              color: '#ffffff',
              border: '1px solid rgba(255,255,255,0.55)',
              padding: '0.5rem 0.9rem',
              borderRadius: '4px',
              fontWeight: 600,
              fontSize: '0.875rem',
              textDecoration: 'none',
              display: 'inline-block',
            }}
          >
            Customize
          </a>
          <button
            ref={dismissRef}
            type="button"
            onClick={acceptEssential}
            aria-label="Dismiss cookie banner (essential only)"
            style={{
              background: 'transparent',
              color: '#ffffff',
              border: '1px solid transparent',
              padding: '0.25rem 0.55rem',
              borderRadius: '4px',
              fontSize: '1.1rem',
              lineHeight: 1,
              cursor: 'pointer',
            }}
          >
            ×
          </button>
        </div>
      </div>
    </div>
  );
}
